<template>
  <NamiModalTitle @close="$emit('resolved')">
    {{ $t("components.modalTitleShare.title") }}
  </NamiModalTitle>
  <div v-bind="$attrs" class="space-y-4">
    <TitleCard :title="title" class="!bg-transparent p-0 shadow-none" />
    <div class="space-y-2">
      <h2 class="text-lg font-medium">
        {{ $t("components.modalTitleShare.shareOnText") }}
      </h2>
      <div class="sm:flex items-center justify-between">
        <ul class="flex items-center gap-2">
          <li v-for="location in shareLocations">
            <NamiButton
              buttonType="secondary"
              text
              :icon="location.icon"
              :href="location.url"
              external
              target="_blank"
            />
          </li>
        </ul>
        <div class="my-2 sm:my-0 flex items-center">
          <NamiButton
            pill
            button-type="secondary"
            :icon="IconCopy"
            small
            :title="currentUrlToCopy"
            @button-click="tryCopyLink"
          >
            <div class="pr-1">
              {{ currentUrlToCopy.slice(0, 32)
              }}{{ currentUrlToCopy.length > 32 ? "..." : "" }}
            </div>
          </NamiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  IconBrandFacebook,
  IconBrandReddit,
  IconBrandTwitter,
  IconCopy,
  IconLink,
} from "@tabler/icons-vue";

import type { TagRelation, TitleEntity } from "~/src/api";

defineComponent({
  inheritAttrs: false,
});

const props = defineProps<{
  title: TitleEntity;
  extraLinkParameters?: Record<string, any>;
}>();

const emit = defineEmits<{
  (e: "resolved"): void;
}>();

const route = useRoute();
const config = useRuntimeConfig();
const { $i18n, $app } = useNuxtApp();
const { t } = $i18n.global;

const appStore = $app();
const locale = useLocale();
const useShortUrl = ref(true);
const titleTitle = computed(() =>
  extractFromLocale(locale.value.iso, props.title.attributes.title),
);

const allTags = computed(() => {
  return (
    props.title.relationships.filter(
      (r): r is TagRelation => r.type === "tag",
    ) ?? ([] as TagRelation[])
  );
});

const primaryAndSecondaryTags = computed(() => {
  const primaryTag = props.title.relationships.find(
    (r): r is TagRelation => r.type === "primary_tag",
  );
  const secondaryTag = props.title.relationships.find(
    (r): r is TagRelation => r.type === "secondary_tag",
  );

  return [primaryTag, secondaryTag].filter(
    <T,>(x: T | undefined): x is T => !!x,
  );
});

function sanitizeTag(tag: string) {
  return tag.replace(/\s/g, "").replace(/'/g, "");
}

const redditShareUrl = computed(() => {
  const url = new URL("https://www.reddit.com/submit");

  url.searchParams.set("url", config.public.baseUrl + linkTo(props.title));

  // let title = `I am reading $title on NamiComi: $MainGenre and $SecondGenre with $Theme1 and $Theme2  themes!`
  let title = `I am reading ${titleTitle.value} on NamiComi:`;

  const genres = primaryAndSecondaryTags.value.map((t) => t.id);
  const themes = allTags.value
    .filter((t) => t.attributes?.group === "theme")
    .map((t) => t.id);

  if (genres[0]) {
    title += ` ${t(`components.tag.${genres[0]}.name`)}`;

    if (genres[1]) {
      title += ` and ${t(`components.tag.${genres[1]}.name`)}`;
    }

    if (themes[0]) {
      title += " with";
    }
  }

  if (themes[0]) {
    title += ` ${t(`components.tag.${themes[0]}.name`)}`;

    if (themes[1]) {
      title += ` and ${t(`components.tag.${themes[1]}.name`)}`;
    }

    title += " themes!";
  }

  url.searchParams.set("title", title);

  return url.href;
});

const twitterShareUrl = computed(() => {
  const url = new URL("https://twitter.com/intent/tweet");

  let text = `Reading ${titleTitle.value} on NamiComi!\n\n${
    config.public.baseUrl + linkTo(props.title)
  }\n\n`;

  if (primaryAndSecondaryTags.value[0]) {
    const tagName1 = sanitizeTag(
      t(`components.tag.${primaryAndSecondaryTags.value[0].id}.name`),
    );

    text += `#${tagName1}`;

    if (primaryAndSecondaryTags.value[1]) {
      const tagName2 = sanitizeTag(
        t(`components.tag.${primaryAndSecondaryTags.value[1].id}.name`),
      );
      text += ` #${tagName2}`;
    }

    text += " #NamiComi";
  } else {
    text += "#NamiComi";
  }

  url.searchParams.set("text", text);

  return url.href;
});

const currentUrlToCopy = computed(() => `https://nami.moe/t/${props.title.id}`);
const tryCopyLink = () => {
  copyTextToClipboard(currentUrlToCopy.value);
  emit("resolved");
};

const shareLocations = computed(() => [
  {
    name: "Facebook",
    url: `https://www.facebook.com/sharer/sharer.php?u=${
      config.public.baseUrl + linkTo(props.title)
    }`,
    icon: IconBrandFacebook,
  },
  {
    name: "Reddit",
    url: redditShareUrl.value,
    icon: IconBrandReddit,
  },
  {
    name: "Twitter",
    url: twitterShareUrl.value,
    icon: IconBrandTwitter,
  },
]);
</script>
